import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import { Box, Tab, Tabs, Typography, useTheme } from '@mui/material'
import { Key, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import SavePDF from '@/assets/images/SavePDFIcon.svg'
import ToothGraphic2 from '@/assets/images/Tooth-graphic-2.svg'
import ToothGraphic3 from '@/assets/images/Tooth-graphic-3.svg'
import ToothGraphic4 from '@/assets/images/Tooth-graphic-4.svg'
import ToothGraphic5 from '@/assets/images/Tooth-graphic-5.svg'
import ToothGraphic6 from '@/assets/images/Tooth-graphic-6.svg'
import Button from '@/components/Button'
import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import Stepper from '@/components/Stepper'
import { useAppSelector } from '@/store'
import { setAllInOneRecipeOutput, setRecipeActiveStep } from '@/store/allInOne'
import {
  useGetCaseDetailsQuery,
  useGetMetadataQuery,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import { setInternalEffectCeramicBrand } from '@/store/fullLayering'
import { getCaseId, isNullorEmpty } from '@/utils'
import { saveRecipeGeneric } from '@/utils/helper'

import AestheticModelRecipe from './recipeOutput/AestheticModelRecipe'
import DentinRecipe from './recipeOutput/DentinRecipe'
import Framework from './recipeOutput/Framework'
import MaskingRecipe from './recipeOutput/MaskingRecipe'
import OpaquerRecipe from './recipeOutput/OpaquerRecipe'
import RecipeOutputPDF from './recipeOutput/RecipeOutputPDF'
import SkinEnamelRecipe from './recipeOutput/SkinEnamelRecipe'
import { ALL_IN_ONE_STEPS } from './utils/constants'

interface MetadataResponse {
  available_ceramic_brands: any
  available_capbrands: any
  available_stainingbrands: any
  [key: string]: any
}

const RecipeOutPut = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'allInOne.recipeOutput',
  })

  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'buttons',
  })
  const [tableKey, setTableKey] = useState<any>()
  const [selectedTab, setSelectedTab] = useState<number | string>(0)
  const [frameworkIndex, SetFrameworkIndex] = useState<number>(0)
  const [dentinCervicalIndex, setDentinCervicalIndex] = useState<number>(0)
  const [dentinMiddleIndex, setDentinMiddleIndex] = useState<number>(0)
  const [dentinIncisalIndex, setDentinIncisalIndex] = useState<number>(0)
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue)
  }
  const caseId = getCaseId() || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')

  const recipeOutput = useAppSelector(
    (state) => state.allInOne.allInOneRecipeOutput,
  )
  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const dispatch = useDispatch()
  const { state }: any = useLocation()

  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()

  useEffect(() => {
    if (isNullorEmpty(state, 'recipe')) {
      dispatch(setAllInOneRecipeOutput(state.recipe))
    }
  }, [])

  const getKey = (input: any) => {
    const key = Object.keys(input)
    let key2
    if (input.recipe_type === 'single dentin') {
      key2 = key.filter((item) => {
        return (
          item !== 'skinenameltype_cervical' &&
          item !== 'skinenameltype_incisal' &&
          item !== 'method' &&
          item !== 'portionertype' &&
          item !== 'prepcolor_cervical' &&
          item !== 'prepcolor_incisal' &&
          item !== 'targetcervical' &&
          item !== 'targetincisal' &&
          item !== 'availspace_cervical' &&
          item !== 'availspace_incisal'
        )
      })
    } else {
      key2 = key.filter((item) => {
        return (
          item !== 'skinenameltype_cervical' &&
          item !== 'skinenameltype_incisal' &&
          item !== 'method' &&
          item !== 'portionertype'
        )
      })
    }

    setTableKey(key2) // key2 for filter
  }
  useEffect(() => {
    if (recipeOutput) {
      if (recipeOutput?.prep_advice) {
        setSelectedTab('Aesthetic Model')
      } else if (recipeOutput?.provided_input?.indication === 'PFM Crown') {
        setSelectedTab('Opaquers')
      } else if (
        recipeOutput?.provided_input?.indication === 'Veneer on refractory'
      ) {
        setSelectedTab('Masking')
      } else if (
        recipeOutput?.provided_input?.indication !== 'PFM Crown' &&
        recipeOutput?.provided_input?.indication !== 'Veneer on refractory'
      ) {
        setSelectedTab('Framework')
      } else {
        setSelectedTab('Dentin')
      }
      getKey(recipeOutput.provided_input)
    }
  }, [recipeOutput])

  const getSelectedRecipeOutput = () => {
    if (!recipeOutput) return null

    const selectedData = { ...recipeOutput }

    if (selectedData.dentinlayer_advice) {
      const dentinAdvice = recipeOutput.dentinlayer_advice

      const cervicalLayer1 =
        dentinAdvice.dentinrecipes_cervical?.recipe_layer1?.[
          dentinCervicalIndex
        ] ?? null
      const cervicalLayer2 =
        dentinAdvice.dentinrecipes_cervical?.recipe_layer2?.[
          dentinCervicalIndex
        ] ?? null

      const middleLayer1 =
        dentinAdvice.dentinrecipes_middle?.recipe_layer1?.[dentinMiddleIndex] ??
        null
      const middleLayer2 =
        dentinAdvice.dentinrecipes_middle?.recipe_layer2?.[dentinMiddleIndex] ??
        null

      const incisalLayer1 =
        dentinAdvice.dentinrecipes_incisal?.recipe_layer1?.[
          dentinIncisalIndex
        ] ?? null
      const incisalLayer2 =
        dentinAdvice.dentinrecipes_incisal?.recipe_layer2?.[
          dentinIncisalIndex
        ] ?? null

      const cervical = {
        ...dentinAdvice.dentinrecipes_cervical,
        recipe_layer1: cervicalLayer1 ? [cervicalLayer1] : null,
        recipe_layer2: cervicalLayer2 ? [cervicalLayer2] : null,
      }

      const middle = {
        ...dentinAdvice.dentinrecipes_middle,
        recipe_layer1: middleLayer1 ? [middleLayer1] : null,
        recipe_layer2: middleLayer2 ? [middleLayer2] : null,
      }

      const incisal = {
        ...dentinAdvice.dentinrecipes_incisal,
        recipe_layer1: incisalLayer1 ? [incisalLayer1] : null,
        recipe_layer2: incisalLayer2 ? [incisalLayer2] : null,
      }

      selectedData.dentinlayer_advice = {
        ...dentinAdvice,
        dentinrecipes_cervical: cervical,
        dentinrecipes_middle: middle,
        dentinrecipes_incisal: incisal,
      }
    }
    if (
      selectedData.internallayer_advice &&
      recipeOutput.provided_input.indication !== 'PFM Crown' &&
      recipeOutput.provided_input.indication !== 'Veneer on refractory'
    ) {
      selectedData.internallayer_advice =
        recipeOutput.internallayer_advice[frameworkIndex]
    }

    return selectedData
  }

  const { data: metaData } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as { data: MetadataResponse | undefined }
  const { data: caseDetails } = useGetCaseDetailsQuery(
    { caseId },
    { skip: !caseId },
  )

  const providedInputNames = metaData?.provided_input_names

  const theme = useTheme()
  const navigate = useNavigate()

  const tabStyles = {
    color: '#999',
    textTransform: 'none',
    '&.Mui-selected': {
      color: '#90CAF9',
    },
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: 700,
    fontFamily: "'Inter'",
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    const filteredOutput = getSelectedRecipeOutput()
    if (!filteredOutput) return

    await saveRecipeGeneric({
      caseId: targetCaseId,
      canvasImage,
      recipeName,
      output: filteredOutput,
      workflow: null,
      mutationFn: saveRecipe,
      recipeType: 'generic',
      closeDialog: () => setSaveRecipedialogOpen(false),
    })
  }

  const generatePdf = async () => {
    const filteredOutput = getSelectedRecipeOutput()
    if (!filteredOutput) return

    await saveRecipeGeneric({
      caseId,
      canvasImage,
      recipeName,
      output: filteredOutput,
      workflow: null,
      mutationFn: savePdf,
      recipeType: 'generic',
      isPdf: true,
    })
  }

  const handleStepClick = (index: number) => {
    if (index === 0 || index === 1) {
      navigate('/all-in-one')
      dispatch(setRecipeActiveStep(index))
    }
  }

  const handleBackClick = () => {
    navigate(`/projects/${caseId}`)
  }
  return (
    <>
      {recipeOutput && (
        <Box display="flex" flexGrow={1} flexDirection="column" zIndex={1111}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={4}
            py={2}
            sx={{
              position: 'relative',
            }}
          >
            {caseDetails?.case_type !== 'auto' && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 32,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Button
                  variant="text"
                  onClick={handleBackClick}
                  sx={{
                    color: '#90CAF9',
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '26px',
                  }}
                  label={t('backToProject')}
                  startIcon={<ChevronLeftIcon />}
                  textTransform="uppercase"
                />
              </Box>
            )}

            <Box display={'flex'} flexGrow={1}>
              <Stepper
                activeStep={3}
                steps={ALL_IN_ONE_STEPS}
                onStepClick={handleStepClick}
              />
            </Box>
          </Box>

          <Box
            sx={{
              height: 'calc(100% - 80px)',
              backgroundColor: theme.palette.background.paper,
              overflowY: 'auto',
              paddingX: 6,
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
                margin: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mx={2}
                mb={3}
                mt={4}
              >
                <Box sx={{ color: 'white', fontSize: 24 }}>
                  {t('yourRecipe')}
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button
                    label={translate('saveAsPdf')}
                    variant="outlined"
                    startIcon={<img src={SavePDF} alt="savePDF" />}
                    onClick={async () => generatePdf()}
                    textTransform="uppercase"
                  />
                  <Button
                    label={translate('saveToProject')}
                    startIcon={<CloudDownloadIcon />}
                    onClick={async () => handleOpenSaveDialog()}
                    textTransform="uppercase"
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: 6,
                  height: 475,
                }}
              >
                {/* Image Container */}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background:
                      'linear-gradient(98deg, #232323 0%, #1A1A1A 100%)',
                    padding: 4,
                    borderRadius: 4,
                    height: 475,
                  }}
                >
                  {(canvasImage?.calibrated_image || canvasImage?.image) && (
                    <img
                      src={canvasImage?.calibrated_image || canvasImage?.image}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        margin: 'auto',
                        height: '411px',
                      }}
                      alt="recipeimage"
                    />
                  )}
                </Box>

                {/* Table Container */}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    padding: 4,
                    background:
                      'linear-gradient(98deg, #232323 0%, #1A1A1A 100%)',
                    borderRadius: 4,
                    height: 475,
                  }}
                >
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                    }}
                  >
                    <tbody>
                      {tableKey?.map(
                        (
                          key: string | number,
                          index: Key | null | undefined,
                        ) => (
                          <tr key={index}>
                            {recipeOutput?.provided_input[key] && (
                              <th style={{ padding: 8, textAlign: 'left' }}>
                                {providedInputNames?.[key]}
                              </th>
                            )}
                            {recipeOutput.provided_input[key] &&
                              (Array.isArray(
                                recipeOutput.provided_input[key],
                              ) ? (
                                <td style={{ padding: 8, textAlign: 'left' }}>
                                  {'L*' +
                                    recipeOutput.provided_input[key][0] +
                                    ', a*' +
                                    recipeOutput.provided_input[key][1] +
                                    ', b*' +
                                    recipeOutput.provided_input[key][2]}
                                </td>
                              ) : (
                                <td style={{ padding: 8, textAlign: 'left' }}>
                                  {recipeOutput.provided_input[key]}
                                </td>
                              ))}
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              // my={4}
              paddingTop="32px"
              paddingBottom="24px"
            >
              <Typography
                sx={{ color: 'white', fontSize: 24, lineHeight: '20px' }}
              >
                {t('recipeDetail')}
              </Typography>
              <Button
                label={t('proceedToInternalEffect')}
                endIcon={<ChevronRightIcon />}
                onClick={() => {
                  if (recipeOutput?.provided_input?.brand) {
                    dispatch(
                      setInternalEffectCeramicBrand(
                        recipeOutput.provided_input.brand,
                      ),
                    )
                  }
                  navigate('/full-layering/internal-effects')
                }}
                textTransform="uppercase"
              />
            </Box>
            <Box
              sx={{
                margin: 'auto',
                maxHeight: 'fit-content',
                // height: 'max(100%, 561px)',
                background: 'linear-gradient(98deg, #232323 0%, #1A1A1A 100%)',
                borderRadius: '16px',
                padding: '32px',
              }}
            >
              {/* Content Section */}
              <Box
                display="flex"
                height="100%"
                sx={{
                  '@media (max-width: 1080px)': {
                    flexDirection: 'column',
                  },
                }}
              >
                <Box
                  flex={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  margin="auto"
                  sx={{ minWidth: '316px' }}
                >
                  {selectedTab === 'Dentin' && (
                    <img
                      style={{ width: '85%' }}
                      src={ToothGraphic4}
                      alt="Toothimage"
                    />
                  )}
                  {selectedTab === 'Skin Enamel' && (
                    <img
                      style={{ width: '85%' }}
                      src={ToothGraphic2}
                      alt="Toothimage"
                    />
                  )}
                  {selectedTab === 'Aesthetic Model' && (
                    <img
                      style={{ width: '85%' }}
                      src={ToothGraphic6}
                      alt="Toothimage"
                    />
                  )}
                  {(selectedTab === 'Opaquers' ||
                    selectedTab === 'Masking' ||
                    selectedTab === 'Framework') && (
                    <img
                      style={{ width: '85%' }}
                      src={ToothGraphic5}
                      alt="Toothimage"
                    />
                  )}
                  {selectedTab === 'Internal Effects' && (
                    <img
                      style={{ width: '85%' }}
                      src={ToothGraphic3}
                      alt="Toothimage"
                    />
                  )}
                </Box>

                {/* Tabs Section */}
                <Box
                  flex={7}
                  sx={{
                    height: 'fit-content',
                  }}
                  display="flex"
                  flexDirection="column"
                  gap="32px"
                >
                  <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    sx={{
                      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    {recipeOutput?.prep_advice && (
                      <Tab
                        label="Aesthetic Model"
                        value="Aesthetic Model"
                        sx={tabStyles}
                      />
                    )}

                    {recipeOutput?.provided_input.indication ===
                      'PFM Crown' && (
                      <Tab label="Opaquers" value="Opaquers" sx={tabStyles} />
                    )}

                    {recipeOutput?.provided_input.indication ===
                      'Veneer on refractory' && (
                      <Tab label="Masking" value="Masking" sx={tabStyles} />
                    )}

                    {['PFM Crown', 'Veneer on refractory'].includes(
                      recipeOutput?.provided_input.indication,
                    ) || (
                      <Tab label="Framework" value="Framework" sx={tabStyles} />
                    )}

                    {['Dentin', 'Skin Enamel'].map((label) => (
                      <Tab
                        key={label}
                        label={label}
                        value={label}
                        sx={tabStyles}
                      />
                    ))}
                  </Tabs>

                  {/* Tab Panels */}

                  {selectedTab === 'Aesthetic Model' &&
                    isNullorEmpty(recipeOutput, 'prep_advice') && (
                      <AestheticModelRecipe
                        recipeOutput={recipeOutput}
                        prep_advice={recipeOutput?.prep_advice}
                        natural_die_material={
                          recipeOutput.provided_input?.natural_die_material
                        }
                      />
                    )}

                  {selectedTab === 'Opaquers' &&
                    recipeOutput.provided_input.indication === 'PFM Crown' && (
                      <OpaquerRecipe
                        internallayer_advice={recipeOutput.internallayer_advice}
                      />
                    )}

                  {selectedTab === 'Framework' &&
                    recipeOutput.provided_input.indication !== 'PFM Crown' &&
                    recipeOutput.provided_input.indication !==
                      'Veneer on refractory' && (
                      <Framework
                        internallayer_advice={recipeOutput.internallayer_advice}
                        frameworkIndex={frameworkIndex}
                        SetFrameworkIndex={SetFrameworkIndex}
                      />
                    )}

                  {selectedTab === 'Masking' &&
                    recipeOutput.provided_input.indication ===
                      'Veneer on refractory' && (
                      <MaskingRecipe
                        internallayer_advice={recipeOutput.internallayer_advice}
                        brand={recipeOutput.provided_input.brand}
                      />
                    )}

                  {selectedTab === 'Dentin' && (
                    <DentinRecipe
                      recipeOutput={recipeOutput}
                      dentinlayer_advice={recipeOutput.dentinlayer_advice}
                      brand={recipeOutput.provided_input.brand}
                      dentinCervicalIndex={dentinCervicalIndex}
                      setDentinCervicalIndex={setDentinCervicalIndex}
                      dentinMiddleIndex={dentinMiddleIndex}
                      setDentinMiddleIndex={setDentinMiddleIndex}
                      dentinIncisalIndex={dentinIncisalIndex}
                      setDentinIncisalIndex={setDentinIncisalIndex}
                      recipeType={recipeOutput.provided_input.recipe_type}
                    />
                  )}

                  {selectedTab === 'Skin Enamel' && (
                    <SkinEnamelRecipe
                      enamellayer_advice={recipeOutput.enamellayer_advice}
                      brand={recipeOutput.provided_input.brand}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {recipeOutput && (
        <RecipeOutputPDF
          recipe={recipeOutput}
          frameworkIndex={frameworkIndex}
          dentinCervicalIndex={dentinCervicalIndex}
          dentinMiddleIndex={dentinMiddleIndex}
          dentinIncisalIndex={dentinIncisalIndex}
          id="download-pdf"
        />
      )}
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject}
        isLoading={isLoading}
      />
    </>
  )
}

export default RecipeOutPut
